import { Form, Formik } from "formik";
import { Plus, Trash } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { Button, Flex, Text } from "@radix-ui/themes";
import SideDrawer from "@/components/ui/SideDrawer";
import InputField from "@/components/form/InputField";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import DropdownMenu from "@/components/ui/DropdownMenu";
import PageTemplate from "@/components/page-template/PageTemplate";
import UserManagementTabs from "@/components/users/UserManagementTabs";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { SkeletonPage } from "@/components/ui/Skeleton";
import ErrorCard from "@/components/ui/ErrorCard";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { useAuth } from "@/providers/AuthProvider";
import { groupService } from "@/services/breeze/GroupService";
import { CreateGroupRequest, Group } from "@/types/breeze/Group";
import { PaginatedResult } from "@/types/breeze/Any";

const GroupsPage = withTenantAdminRequired(function GroupsPage() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentTenant } = useAuth();

  const {
    data: groups,
    isLoading,
    error,
  } = useQuery<Group[]>({
    queryKey: ["groups"],
    queryFn: () => groupService.listGroups(),
  });

  const createGroup = useMutation({
    mutationFn: (group: CreateGroupRequest) => groupService.createGroup(group),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setIsDrawerOpen(false);
    },
  });

  const deleteGroup = useMutation({
    mutationFn: (id: number) => groupService.deleteGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  const sortedGroups = useMemo(() => {
    if (!groups) return [];
    return [...groups].sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  const paginatedGroups: PaginatedResult<Group> = useMemo(
    () => ({
      items: sortedGroups,
      page: 1,
      page_size: sortedGroups.length,
      total_items: sortedGroups.length,
      total_pages: 1,
      has_next_page: false,
      has_previous_page: false,
    }),
    [sortedGroups],
  );

  const columnDefs: DataGridColumn<Group>[] = [
    {
      field: "name",
      header: "Name",
      flex: 1,
      minWidth: 200,
      cellRenderer: (g) => (
        <Link
          to={`/org/${currentTenant?.id}/settings/groups/${g.id}`}
          style={{ color: "var(--accent-11)", textDecoration: "underline" }}
        >
          {g.name}
        </Link>
      ),
    },
    {
      field: "description",
      header: "Description",
      flex: 2,
      minWidth: 240,
      cellRenderer: (g) => (
        <Text size="2" color="gray">
          {g.description ?? "—"}
        </Text>
      ),
    },
    {
      field: "created_at",
      header: "Created At",
      flex: 0.8,
      minWidth: 130,
      cellRenderer: (g) => (
        <Text>{new Date(g.created_at).toLocaleDateString()}</Text>
      ),
    },
  ];

  if (isLoading) return <SkeletonPage title="User Management" />;
  if (error) return <ErrorCard error="Failed to load groups" />;

  return (
    <PageTemplate
      title="User Management"
      description="Accounts, the roles that grant them access, and the groups they belong to"
    >
      <UserManagementTabs active="groups" />
      <Flex justify="end" align="center" mb="4" gap="3">
        <Button onClick={() => setIsDrawerOpen(true)} size="2">
          <Plus className="w-4 h-4" />
          Create Group
        </Button>
      </Flex>

      <DataGrid
        data={paginatedGroups}
        columns={columnDefs}
        storageKey="groups"
        isLoading={isLoading}
        currentPage={1}
        currentPageSize={sortedGroups.length}
        height="auto"
        showPagination={false}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        actions={(g) => (
          <DropdownMenu
            items={[
              {
                label: "Delete",
                icon: <Trash size={16} style={{ color: "var(--red-9)" }} />,
                onClick: async () => {
                  const confirmed = await confirm({
                    title: "Delete Group",
                    message: `Are you sure you want to delete the group "${g.name}"?`,
                    confirmText: "Delete",
                    cancelText: "Cancel",
                    variant: "danger",
                  });
                  if (confirmed) deleteGroup.mutate(g.id);
                },
                disabled: g.is_system,
              },
            ]}
          />
        )}
      />

      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Create New Group"
      >
        <div className="p-4">
          <Formik
            initialValues={{ name: "", description: "" }}
            validationSchema={Yup.object({
              name: Yup.string().required("Name is required"),
              description: Yup.string(),
            })}
            onSubmit={(values) => createGroup.mutate(values)}
          >
            <Form className="space-y-4">
              <InputField name="name" label="Group Name" required />
              <InputField name="description" label="Description" />
              <div className="pt-4">
                <Button type="submit" loading={createGroup.isPending}>
                  Create Group
                </Button>
              </div>
            </Form>
          </Formik>
        </div>
      </SideDrawer>
    </PageTemplate>
  );
});

export default GroupsPage;
